import React from 'react';

import { Input, DropdownButton, Toggle, Label } from '@ergeon/core-components';
import {
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  VideoCameraIcon,
  MapPinIcon,
  DocumentIcon,
  QueueListIcon,
  MicrophoneIcon,
  ChatBubbleBottomCenterTextIcon,
  ClipboardDocumentListIcon,
  InformationCircleIcon,
} from '@ergeon/icons/hero/micro';

import { showUpcomingFeatures } from '../../../../utils';
import { UnifiedDialogEntry } from '../../../../types';

type DialogFiltersProps = {
  activeFilter: string;
  setActiveFilter: (filter: string) => void;
  showUnreads: 'all' | 'unreads';
  setShowUnreads: (value: 'all' | 'unreads') => void;
  searchText: string;
  setSearchText: (text: string) => void;
  entries: UnifiedDialogEntry[];
};

const DialogFilters: React.FC<DialogFiltersProps> = ({
  activeFilter,
  setActiveFilter,
  showUnreads,
  setShowUnreads,
  searchText,
  setSearchText,
  entries,
}) => {
  const filterIcons: Record<string, React.ReactNode> = {
    All: <QueueListIcon className="size-4" />,
    Calls: <PhoneIcon className="size-4" />,
    Transcripts: <ChatBubbleBottomCenterTextIcon className="size-4" />,
    SMS: <ChatBubbleLeftRightIcon className="size-4" />,
    Voicemails: <MicrophoneIcon className="size-4" />,
    Emails: <EnvelopeIcon className="size-4" />,
    LVE: <VideoCameraIcon className="size-4" />,
    Notes: <ClipboardDocumentListIcon className="size-4" />,
    Context: <InformationCircleIcon className="size-4" />,
    Pins: <MapPinIcon className="size-4" />,
    Files: <DocumentIcon className="size-4" />,
  };

  // Determine available filters based on entries
  const hasCallEntries = entries.some((e) =>
    ['call_header', 'call_segment', 'call_missed', 'call_abandoned'].includes(e.entry_type),
  );
  const hasTranscripts = entries.some((e) =>
    ['call_segment', 'lve_segment', 'voicemail_segment'].includes(e.entry_type),
  );
  const hasVoicemailEntries = entries.some((e) =>
    ['voicemail_header', 'voicemail_segment', 'voicemail_simple'].includes(e.entry_type),
  );
  const hasLVEEntries = entries.some((e) => ['lve_header', 'lve_segment'].includes(e.entry_type));
  const hasNoteEntries = entries.some((e) => e.entry_type === 'internal_note');
  const hasContextEntries = entries.some((e) => e.entry_type === 'context');

  const availableFilters = ['All', 'SMS', 'Emails'];
  if (hasCallEntries) availableFilters.push('Calls');
  if (hasTranscripts) availableFilters.push('Transcripts');
  if (hasVoicemailEntries) availableFilters.push('Voicemails');
  if (hasLVEEntries) availableFilters.push('LVE');
  if (hasNoteEntries) availableFilters.push('Notes');
  if (hasContextEntries) availableFilters.push('Context');

  const dropdownItems = availableFilters.map((filter) => ({
    label: filter,
    icon: filterIcons[filter],
    onClick: () => setActiveFilter(filter),
    selected: filter === activeFilter,
  }));

  return (
    <div className="flex justify-between items-center gap-4 w-full">
      <DropdownButton
        alignment="left"
        flavor="regular"
        hideChevron={false}
        items={dropdownItems}
        label={activeFilter}
        size="small"
        taste="boundless"
        title="Filter messages" />
      <div className="flex items-center gap-4 w-full max-w-96">
        {showUpcomingFeatures('APPS-5873') && (
          <div className="flex items-center gap-2">
            <Label>Unreads</Label>
            <Toggle
              items={[
                { label: '', value: 'all' as const },
                { label: '', value: 'unreads' as const },
              ]}
              onChange={(value) => setShowUnreads(value as 'all' | 'unreads')}
              value={showUnreads} />
          </div>
        )}

        <Input
          id="message-search"
          label="Filter by Text"
          name="message-search"
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Type to search in events..."
          size="small"
          value={searchText} />
      </div>
    </div>
  );
};

export default DialogFilters;
